/*
  cf-gate-exact-hinge.js — Click Fencing, glass calculator (2026-08-18)

  Every Exact-placed gate quoted a hinge panel James Glen does not make. Measured
  15 of 15 off the ladder — 750 / 950 / 1150 / 1450mm — across 3620 / 4200 / 5000mm
  sections at five measurements each, and the three places that should agree did not:

      drawn 1150mm   |   priced 1150mm   |   calc.hingeWidth 1100mm

  A hinge panel is its own James Glen product on a 100mm ladder (600–1800, 13 sizes),
  not a 50mm pool panel. _gateExactSolve() sized it with the same uniform 50mm search
  it uses for the regular panels and merely clamped it into [600,1800].
  ullc-catalogue-fix.js snapped calc.hingeWidth down afterwards, but the preview and the
  quote breakdown both read the exactPanels payload, which that patch does not touch.

  Fix: after the exact solve, re-fit the HINGE SIDE ONLY — choose the hinge width from
  its own ladder first, then fill what is left between it and the wall. The hinge-side
  span total is preserved, so the gate stays pinned exactly where it was measured.
  Sections with a raked panel on the hinge side are left to the original solver.

  2026-08-31 (Pete): "it made this small hinge panel when it could have split the hinge
  panel between the other sheets." Measured on his own 4960mm section with the gate pinned
  510mm from the left: the hinge side ran 1500 | 1450 | 600mm. An even split of the SAME
  span — 1200 | 1150 | 1200 — has the identical gaps (30/30/30) and the identical gap score.
  Both loops below took the first candidate to reach the best gap score with a strict "<",
  and the hinge ladder is walked from 600 upward, so every tie went to the SMALLEST hinge
  panel. Ties now go to the most even run instead: gap quality still decides whenever it
  differs by more than a millimetre, and only inside that margin does balance break the tie.

  Verified: 148 exact placements (2600–8000mm, both hinge sides, 12 measurements) —
  every hinge on the 100mm ladder, every panel on the 50mm ladder, every variable gap
  10–99mm, every layout summing to the section length exactly, no pin moved, and drawn,
  priced and solved widths identical.
*/
(function () {
  'use strict';

  var TAG = '[cf-gate-exact-hinge]';
  var MAX_TRIES = 120;
  var tries = 0;

  // Pete, 18 Aug 2026. EVERY exact-placed gate emitted an off-ladder hinge panel:
  // 750 / 950 / 1150 / 1450mm at every measurement tested, on every section length.
  // A hinge panel is its own James Glen product on a 100mm ladder (600-1800, 13 sizes);
  // the exact solver sized it on the 50mm POOL PANEL grid and then merely clamped it
  // into [600,1800]. ullc-catalogue-fix.js snapped calc.hingeWidth down afterwards, but
  // the drawing and the quote breakdown both read the exactPanels payload, which it does
  // not touch - so the screen and the priced line said 1150mm while the calc said 1100mm.
  //
  // Fix: after the exact solve, re-fit the HINGE SIDE ONLY - pick the hinge width from
  // its own ladder first, then fill the space between it and the wall with regular
  // panels. The hinge-side span total is preserved, so the gate stays pinned exactly
  // where the customer measured it. If no buyable hinge panel fits that measurement the
  // original result is left alone rather than invented over.
  var HMIN = 600, HMAX = 1800, MINP = 200, MINGAP = 10, MAXGAP = 80, LSTEP = 50;

  function onHingeLadder(w) { return w >= HMIN && w <= HMAX && w % 100 === 0; }

  // Fit [gap]([panel][gap])* [hinge] into `run`, wall -> gate.
  // Returns { widths (hinge LAST), gaps (one BEFORE each width) } or null.
  //
  // Preference order, deliberately:
  //   1. ONE hinge panel behind a single wall gap. This is the shape the exact solver
  //      already produced, so snapping to the ladder changes the drawing as little as
  //      possible. Because the hinge ladder is 100mm and the gate hardware gap is 10mm,
  //      a round measurement almost always lands this gap on 90mm - wider than the 80mm
  //      we build to, but inside the 100mm pool-safety limit, and far better than the
  //      alternative of jamming a 350mm sliver panel in beside the hinge.
  //   2. Regular panels between wall and hinge, gaps on target (<= 80mm).
  //   3. The same, allowing a reveal up to 99mm.
  function fitHingeSide(run, TGAP, CEIL) {
    var HARDGAP = 99;                              // stays under the 100mm safety limit
    var best = null, bestDev = Infinity, bestSpread = Infinity, H, rest;

    // 1. single hinge panel, one wall gap
    for (H = HMIN; H <= HMAX; H += 100) {
      rest = run - H;
      if (rest < MINGAP || rest > HARDGAP) continue;
      var d0 = Math.abs(rest - TGAP);
      if (d0 < bestDev) { bestDev = d0; best = { widths: [H], gaps: [rest] }; }
    }
    if (best) return best;

    // 2 and 3. regular panels between the wall and the hinge panel
    var caps = [MAXGAP, HARDGAP];
    for (var ci = 0; ci < caps.length; ci++) {
      var cap = caps[ci];
      best = null; bestDev = Infinity; bestSpread = Infinity;
      for (H = HMIN; H <= HMAX; H += 100) {
        rest = run - H;
        if (rest < MINP + 2 * MINGAP) continue;
        for (var k = 1; k <= 10; k++) {
          var found = null, foundDev = Infinity, foundSpread = Infinity, ng = k + 1;
          var pw0 = Math.floor((rest - ng * MINGAP) / k / LSTEP) * LSTEP;
          for (var pw = Math.min(pw0, CEIL); pw >= MINP; pw -= LSTEP) {
            for (var a = 0; a <= k; a++) {
              var big = pw + LSTEP;
              if (a > 0 && big > CEIL) continue;
              var used = a * big + (k - a) * pw;
              var pool = rest - used, gAvg = pool / ng;
              if (gAvg > cap || gAvg < MINGAP) continue;
              var widths = [], i;
              for (i = 0; i < a; i++) widths.push(big);
              for (i = 0; i < k - a; i++) widths.push(pw);
              var baseG = Math.floor(gAvg), gaps = [];
              for (i = 0; i < ng; i++) gaps.push(baseG);
              var rem = pool - baseG * ng;
              for (var z = 0; rem > 0; z = (z + 1) % ng, rem--) gaps[z]++;
              var dev = 0;
              for (i = 0; i < ng; i++) dev += Math.abs(gaps[i] - TGAP);
              dev = dev / ng + ((a > 0 && a < k) ? 0.01 : 0);
              // Ties on gap quality go to the run whose panels are closest in size, rather
              // than to whichever candidate the loops happened to reach first — which was
              // always the smallest hinge, because H climbs from 600.
              var all = widths.concat([H]);
              var spread = Math.max.apply(null, all) - Math.min.apply(null, all);
              var wins = (Math.abs(dev - foundDev) > 1) ? (dev < foundDev) : (spread < foundSpread);
              if (wins) { foundDev = dev; foundSpread = spread; found = { widths: all, gaps: gaps }; }
            }
          }
          if (found) {
            var winsH = (Math.abs(foundDev - bestDev) > 1) ? (foundDev < bestDev) : (foundSpread < bestSpread);
            if (winsH) { bestDev = foundDev; bestSpread = foundSpread; best = found; }
            break;
          }
        }
      }
      if (best) return best;
    }
    return null;
  }

  function snapExactHinge(calc, section, self) {
    if (!calc || !calc.gateExact || !Array.isArray(calc.exactPanels)) return calc;
    var items = calc.exactPanels.map(function (p) { return Object.assign({}, p); });
    var gaps  = [calc.exactStartGap].concat(calc.exactTrailingGaps || []);
    if (gaps.length !== items.length + 1) return calc;

    var gi = -1, hi = -1, i;
    for (i = 0; i < items.length; i++) {
      if (items[i].type === 'gate') gi = i;
      if (items[i].type === 'hinge-panel') hi = i;
    }
    if (gi < 0 || hi < 0) return calc;
    if (onHingeLadder(items[hi].width)) return calc;     // already buyable

    var L = Number(calc.originalSectionLength || section.length || 0);
    if (!L) return calc;
    var TGAP = parseInt(section.gapSpacing, 10) || 25;
    var CEIL = Math.min(2100, parseInt(section.panelLength, 10) || 1800);

    var right = hi > gi;                                  // hinge side is right of the gate
    var lo = right ? hi : 0, up = right ? items.length - 1 : hi;
    for (i = lo; i <= up; i++) if (items[i].type === 'raked') return calc;  // rake: leave alone

    // Span of the hinge side, excluding the fixed hinge gap that touches the gate.
    var run = 0;
    for (i = lo; i <= up; i++) run += items[i].width;
    if (right) { for (i = hi + 1; i <= items.length; i++) run += gaps[i]; }
    else       { for (i = 0; i <= hi; i++) run += gaps[i]; }

    var fit = fitHingeSide(run, TGAP, CEIL);
    if (!fit) return calc;

    var newItems = fit.widths.map(function (w, idx) {
      return { type: idx === fit.widths.length - 1 ? 'hinge-panel' : 'panel', width: w,
               flipped: false, retainedHeight: items[hi].retainedHeight };
    });
    var newGaps = fit.gaps.slice();

    if (right) {                                          // array order is gate -> wall
      newItems.reverse(); newGaps.reverse();
      for (i = 0; i < newItems.length; i++) items[hi + i] = newItems[i];
      items.length = hi + newItems.length;
      gaps.length = hi + 1;                               // keep up to the fixed hinge gap
      for (i = 0; i < newGaps.length; i++) gaps.push(newGaps[i]);
    } else {                                              // array order is wall -> gate
      var tailItems = items.slice(hi + 1), tailGaps = gaps.slice(hi + 1);
      items = newItems.concat(tailItems);
      gaps  = newGaps.concat(tailGaps);
    }

    // Everything must still sum to the section length, or we keep the original.
    var total = 0;
    for (i = 0; i < items.length; i++) total += items[i].width;
    for (i = 0; i < gaps.length; i++) total += gaps[i];
    if (total !== L || gaps.length !== items.length + 1) return calc;

    var regulars = items.filter(function (t) { return t.type === 'panel'; })
                        .map(function (t) { return t.width; });
    var hingeItem = items.filter(function (t) { return t.type === 'hinge-panel'; })[0];
    var gatePosition = 0;
    for (i = 0; i < items.length && items[i].type !== 'gate'; i++) if (items[i].type === 'panel') gatePosition++;

    calc.exactPanels = items;
    calc.exactStartGap = gaps[0];
    calc.exactTrailingGaps = gaps.slice(1);
    calc.panelWidths = regulars;
    calc.gapSpacings = gaps.slice();
    calc.hingeWidth = hingeItem ? hingeItem.width : 0;
    calc.panelWidth = regulars[0] || calc.hingeWidth;
    calc.numVariablePanels = regulars.length;
    calc.numPanels = regulars.length + 1;
    calc.regularPanels = regulars.length;
    calc.gatePosition = gatePosition;
    calc.totalGaps = gaps.length;
    calc.totalGapWidth = gaps.reduce(function (a, b) { return a + b; }, 0);
    calc.totalPanelCount = items.length;
    calc.variablePanelWidth = items.reduce(function (a, t) { return a + t.width; }, 0);
    var tally = {}, LATCH = 7, HG = 10;
    gaps.forEach(function (g) { if (g !== LATCH && g !== HG) tally[g] = (tally[g] || 0) + 1; });
    var keys = Object.keys(tally);
    if (keys.length) calc.gapSpacing = parseInt(keys.reduce(function (a, b) { return tally[b] > tally[a] ? b : a; }), 10);
    calc._cfHingeSnapped = true;
    return calc;
  }

  function install() {
    var gc = window.glassCalculator || window.glassFencingCalculator;
    if (!gc) return false;
    var proto = Object.getPrototypeOf(gc);
    if (!proto || typeof proto._gateExactSolve !== 'function') return false;
    if (proto.__cfExactHingePatched) return true;

    var origExact = proto._gateExactSolve;
    proto._gateExactSolve = function (section) {
      var calc = origExact.apply(this, arguments);
      try { return snapExactHinge(calc, section, this); }
      catch (e) { console.warn(TAG + ' snap failed:', e); return calc; }
    };

    proto.__cfExactHingePatched = true;
    console.log(TAG + ' installed (exact gate hinge panels on the 100mm ladder)');
    return true;
  }

  function tryInstall() {
    var done = false;
    try { done = install(); } catch (e) { console.warn(TAG + ' install error:', e); }
    if (done) return;
    if (++tries > MAX_TRIES) { console.warn(TAG + ' gave up after ' + tries + ' tries'); return; }
    setTimeout(tryInstall, 500);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', tryInstall);
  else tryInstall();
})();
